import type { ChatMessage } from '@elara/client-core/models/ChatMessageModel';
import type { MessageUiState } from '@elara/client-core/states/MessageUiState';

import React from 'react';

import { replaceLatexSymbols } from '@elara/client-core/chat/latex-symbols';
import { formatDateTime } from '@elara/client-core/lib/date-utils';
import { cn } from '@elara/client-core/lib/utils';

import { openFeedbackDialog } from '#lib/sentry-feedback.js';
import { useMessageViewState } from '#states/hooks.js';
import { CopyButton } from '#ui/copy-button.jsx';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconSizes,
  MaterialIcon,
} from '#ui/index.js';

interface MessageFeedbackBarProps {
  msg: ChatMessage;
  state: MessageUiState;
}

export function MessageFeedbackBar({ msg, state }: MessageFeedbackBarProps) {
  const messageViewState = useMessageViewState();

  // Prefer server-backed feedback so realtime Firestore updates (e.g. after submitting bad feedback) show immediately
  const displayRating = msg.feedback?.rating;

  if (!msg.isModelMessage) return null;
  if (!msg.chatId || !msg.id) return null;

  function handleGoodClick() {
    void messageViewState.submitFeedback(msg.chatId!, msg.id, 'good');
  }

  function handleBadClick() {
    messageViewState.openFeedbackDialogForMessage(msg.chatId!, msg.id);
  }

  return (
    <>
      <div className={cn('-ml-2 mt-1 flex items-center gap-0.5 text-muted-foreground')}>
        <CopyButton
          size="icon-sm"
          variant="icon"
          tooltip="Copy message"
          getContent={() => replaceLatexSymbols(state.formattedText)}
          aria-label="Copy message"
        />
        <Button
          variant="icon"
          size="icon-sm"
          aria-label="Rate response as helpful"
          tooltip="Rate response as helpful"
          onClick={handleGoodClick}
          disabled={displayRating === 'good'}
          className={cn(displayRating && displayRating === 'bad' && 'hidden')}
        >
          <MaterialIcon icon="thumb_up" size={IconSizes.xs} filled={displayRating === 'good'} />
        </Button>
        <Button
          variant="icon"
          size="icon-sm"
          aria-label="Rate response as not helpful"
          tooltip="Rate response as not helpful"
          onClick={handleBadClick}
          disabled={displayRating === 'bad'}
          className={cn(displayRating && displayRating === 'good' && 'hidden')}
        >
          <MaterialIcon icon="thumb_down" size={IconSizes.xs} filled={displayRating === 'bad'} />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="icon" size="icon-sm" aria-label="More actions" tooltip="More actions">
              <MaterialIcon icon="more_horiz" size={IconSizes.xs} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel className="font-normal text-muted-foreground">
              {formatDateTime(msg.createdAt, 'MMM d, yyyy', 'h:mm a')}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void openFeedbackDialog()}>
              <MaterialIcon icon="bug_report" className="mr-2" size={IconSizes.sm} />
              Report a bug
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
